import React from "react";
import { Section, Text, Link } from "@/components";
import { Paragraph, LineBreak, Quote, SubHeading } from "./Common";

export const DataUsePolicy = (): JSX.Element => {
  return (
    <div className="h-[calc(100vh-152px)] overflow-y-auto">
      <Section className="mx-auto max-w-[1191px] justify-start">

        <div className="mb-14">
          <Text.Heading className="text-left mb-6">Commercial Data Use Policy</Text.Heading>
          <Paragraph>
            Updated: March 31st 2025
            <LineBreak />
            This commercial data use policy ("<Quote>Data Policy</Quote>") is issued by <Quote>TWO12 Inc.</Quote>
            ("<Quote>T12</Quote>", "<Quote>we</Quote>", "<Quote>us</Quote>") 
            and is incorporated into the Terms of Use. This Data Policy applies to commercial data supplied by users of the suite of online services, 
            tools and outputs made available at <Link to="https://www.intelliroom.ai" className="!p-0"><Quote>www.intelliroom.ai</Quote></Link> (the “<Quote>Platform</Quote>” and the “<Quote>Services</Quote>” respectively), 
            including any related APIs.
          </Paragraph>
        </div>
                
        <div  className="mb-14">
          <SubHeading>1. Purpose and Scope</SubHeading>
          <Paragraph>
            This Data Policy governs how T12 deals with non-personal, user-supplied commercial data submitted to the Platform, 
            including documents and data files (“<Quote>Commercial Data</Quote>”).
            <LineBreak />
            Prompts and queries submitted by users in order to use the Services may also constitute Commercial Data if: (a) they contain sensitive commercial information not generally available to the public or otherwise included in the training data of our models; and (b) identify the user or any other party. 
            <LineBreak />
            Operational usage data (data which identifies how users interact with the Platform and the Services), including but not limited to (i) syntax of user inputs; (ii) frequency and duration of use; (iii) classification of prompts and queries, which are used by us to improve the functionality of the Services or the Platform are not subject to this Data Policy unless such usage data includes Commercial Data.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>2. Ownership and Rights</SubHeading>
          <Paragraph>
            As between you and T12, you retain all ownership and intellectual property rights in:
            <ul className="list-disc list-inside ml-12">
              <li>
                Documents and data files you upload; and
              </li>
              <li>
                Any other proprietary business content provided in your inputs to the Platform.
              </li>
            </ul>
            <LineBreak />
            (“<Quote>User Content</Quote>”) <br />
            T12 does not claim any ownership or other proprietary interest in your Commercial Data or User Content, nor any right to use the same except as required for the provision of the Services.
            <LineBreak />
            Use of the Services does not guarantee the rights or exclusivity in the output generated by the Platform, particularly where such output is not directly derivative of your input.
          </Paragraph>
        </div>
        <div className="mb-14">
          <SubHeading>3. Prompts and Queries</SubHeading>
          <Paragraph>
            Prompts and queries submitted via text input which do not contain or are treated as Commercial Data under section 1 of this Data Policy are functional commands, and are not treated as proprietary but shall be treated as confidential in any event. We may store and analyse prompt content to maintain, improve, or troubleshoot the Services and the Platform, but we shall not share, publish or disclose the same. 
            <LineBreak />
            Where prompts or queries include personal data, such data may fall outside the scope of relevant data protection legislation if it is processed only transiently and not stored. Personal data included in prompts and queries is processed in accordance with our Personal Data Privacy Policy here.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>4. Hosting, Use Limitations, Confidentiality</SubHeading>
          
          <div className="ml-6 mb-7">
            <SubHeading>4.1 Hosting</SubHeading>
            <Paragraph>
              Our AI functionality is powered by Amazon Web Services OpenAI Service, operated entirely within Amazon’s secure cloud environment. This means your data is processed and stored within a Amazon-controlled infrastructure, isolated from public APIs or training environments. Input and output data are not used to train or retrain language models, ensuring your Commercial Data remains confidential and segregated.
            </Paragraph>
          </div>

          <div className="ml-6 mb-7">
            <SubHeading>4.2 Commercial Data Use Limitations</SubHeading>
            <Paragraph>
              T12 will not:<br />
              <ul className="list-disc list-inside ml-12">
                <li>
                  Use your Commercial Data to train or retrain AI models
                </li>
                <li>
                  Share or sell your Commercial Data to third parties
                </li>
                <li>
                  Reuse your Commercial Data for unrelated services or analysis
                </li>
              </ul>
            </Paragraph>
          </div>

          <div className="ml-6">
            <SubHeading>4.3 Confidentiality</SubHeading>
            <Paragraph>
              T12 treats all uploaded content and user input as confidential, regardless of whether it is or contains Commercial Data. Access is limited to authorised personnel for technical support and maintenance. Business and commercial staff do not have access to your User Content in any event.
            </Paragraph>
          </div>
        </div>

        <div className="mb-14">
          <SubHeading>5. Output Use and Limitations</SubHeading>
          
          <div className="ml-6 mb-7">
            <SubHeading>5.1 Derivative Output</SubHeading>
            <Paragraph>
              Output directly based on your uploaded documents is treated as derivative of User Content and, as between you and T12, owned by you. The extent to which our models have modified the original User Content, such output is considered provided for internal business use strictly in accordance with the applicable Terms of Use for the Services. This does not grant ownership over any training data, underlying models, or model-generated output generated independently of your User Content.
            </Paragraph>
          </div>

          <div className="ml-6">
            <SubHeading>5.2 Model-Generated Output</SubHeading>
            <Paragraph>
              Output generated independently of User Content (e.g., based on general queries) is licensed to you “<Quote>as is</Quote>” on a non-exclusive, royalty-free basis for internal use only, without warranty of originality or exclusivity of such output.
            </Paragraph>
          </div>
        </div>

        <div className="mb-14">
          <SubHeading>6. Retention and Deletion</SubHeading>
          <Paragraph>
            You may delete your uploaded User Content via the functionality of the Platform. Upon deletion, the User Content is permanently removed from active systems. 
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>7. Enterprise Deployment</SubHeading>
          <Paragraph>
            Where offered, T12 may provide private or on-premise deployment options for enterprise clients. These deployments are intended to support data sovereignty, enhanced control, and integration with internal governance frameworks.
            <LineBreak />
            On-premise deployments, where applicable, may involve the installation and operation of our services within the client’s own infrastructure. These options are only available under separate agreement and may be subject to specific technical, security, and support conditions as mutually agreed in writing.
            <LineBreak />
            T12 does not guarantee availability of on-premise deployment in all cases and reserves the right to determine eligibility and scope based on technical feasibility and commercial terms.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>8. Contact</SubHeading>
          <Paragraph>
            Questions about this policy can be directed to:<br />
            <Link to="mailto:support@intelliroom.ai" className="!p-0"><Quote>support@intelliroom.ai</Quote></Link>
          </Paragraph>
        </div>
      </Section>
    </div>
  );
};
